

import { useState, useEffect } from "react";
import { MoreVertical, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@repo/ui/components/ui/button";
//@ts-ignore
import { createPortal } from "react-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui/components/ui/popover";

import { Calendar } from "@repo/ui/components/ui/calendar";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { TimeRange, DateRange, timeRangeOptions } from "../types";

interface TimeRangeDropdownProps {
  value: TimeRange;
  onChange: (range: TimeRange, dateRange?: DateRange) => void;
  customDateRange?: DateRange;
}

export function TimeRangeDropdown({ value, onChange, customDateRange }: TimeRangeDropdownProps) {
  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | undefined>(customDateRange?.startDate);
  const [tempEndDate, setTempEndDate] = useState<Date | undefined>(customDateRange?.endDate);

  // Sync temp dates with customDateRange prop changes
  useEffect(() => {
    setTempStartDate(customDateRange?.startDate);
    setTempEndDate(customDateRange?.endDate);
  }, [customDateRange]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDateDialogOpen) {
        setIsDateDialogOpen(false);
      }
    };

    if (isDateDialogOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isDateDialogOpen]);

  const handleRangeSelect = (range: TimeRange) => {
    if (range === 'custom') {
      setIsDateDialogOpen(true);
    } else {
      onChange(range);
    }
  };

  const handleCustomDateApply = () => {
    if (tempStartDate && tempEndDate) {
      onChange('custom', { startDate: tempStartDate, endDate: tempEndDate });
      setIsDateDialogOpen(false);
    }
  };

  const handleDialogClose = () => {
    setIsDateDialogOpen(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            <MoreVertical className="h-4 w-4 text-gray-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {timeRangeOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleRangeSelect(option.value)}
              className={value === option.value ? 'bg-gray-100' : ''}
            >
              {option.label}
              {value === option.value && option.value === 'custom' && customDateRange?.startDate && (
                <span className="ml-2 text-xs text-gray-500">
                  ({format(customDateRange.startDate, 'dd/MM', { locale: fr })})
                </span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Custom Date Range Modal */}
      {isDateDialogOpen && typeof window !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleDialogClose();
            }
          }}
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50" />

          {/* Modal Content */}
          <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-lg shadow-lg">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Sélectionner une période</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium block mb-2">Date de début</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {tempStartDate ? format(tempStartDate, 'dd/MM/yyyy', { locale: fr }) : 'Choisir'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={tempStartDate}
                          onSelect={(date) => setTempStartDate(date)}
                          locale={fr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <label className="text-sm font-medium block mb-2">Date de fin</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {tempEndDate ? format(tempEndDate, 'dd/MM/yyyy', { locale: fr }) : 'Choisir'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={tempEndDate}
                          onSelect={(date) => setTempEndDate(date)}
                          locale={fr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" size="sm" onClick={handleDialogClose}>
                    Annuler
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCustomDateApply}
                    disabled={!tempStartDate || !tempEndDate}
                  >
                    Appliquer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
